import React, { useState, useEffect, useMemo } from 'react';
import { SOLUTIONS_TASKS_COLUMNS } from 'constants/reports';
import { SolutionTaskData } from 'store/ducks/Reporting/types';
import { useFilterOptions } from 'utils/hooks/useFilterOptions';
import { useSelector } from 'react-redux';
import { RootState } from 'store/configureStore';
import Helpers from 'utils/Helpers';
import ApiService from 'utils/ApiCalls';
import CustomTable from 'components/Tables/CustomTable';
import * as Styled from './SolutionsTasks.styled';
import { useModuleOptions } from 'utils/hooks/useModule';
import { useReportExport } from 'utils/hooks/useReportExport';
import { stripHtmlTags } from 'utils/stripHtmlTags';

const SolutionsTasks: React.FC = () => {
  const { loading } = useFilterOptions();
  const [data, setData] = useState<SolutionTaskData[]>([]);
  const { registerReport, clearReport } = useReportExport();

  const columns = useMemo(() => SOLUTIONS_TASKS_COLUMNS, []);

  const { orgContext, lang } = useSelector((state: RootState) => ({
    orgContext: state.global.hierarchicalLevelsScope,
    lang: state.global.lang
  }));

  const filterParams = Helpers.toApiFilters(orgContext);
  const normalizedFilters = Helpers.normalizeFilters(
    Helpers.toApiFilters(orgContext)
  );
  const industry = orgContext?.industryType || [];

  const { moduleOptionsState, fetchModules } = useModuleOptions(industry);

  const handleDisplay = async () => {
    try {
      if (!orgContext || Object.keys(orgContext).length === 0) return;
      const hasFilters = Object.keys(filterParams).length > 0;
      if (!hasFilters) return;

      // Get modules first
      const modules =
        moduleOptionsState.length > 0
          ? moduleOptionsState
          : await fetchModules();

      const selectedModule = modules.find(
        (module) => Number(module.value) === Number(normalizedFilters.module)
      );

      const apiFilters = {
        ...normalizedFilters,
        module: selectedModule?.label ?? normalizedFilters.module
      };

      const response = await ApiService.getSolutionsTasks(lang, apiFilters);

      const responseData = Array.isArray(response.data)
        ? response.data
        : response.data?.data || [];

      const formattedData = responseData.map(
        (item: SolutionTaskData, index: any) => {
          const tasks = Object.values(item.TASKS || {}) as Array<{
            task_description: string;
          }>;

          return {
            ...item,
            key: item.sol_id || index,
            task_description: tasks.map((t) =>
              stripHtmlTags(t.task_description)
            ),
            task_type_name: tasks.map((t: any) => t.task_type_name),
            task_status_name: tasks.map((t: any) => t.task_status_name),
            task_owner_name: tasks.map((t: any) => t.task_owner_name),
            task_due_date: tasks.map((t: any) => t.task_due_date),
            task_created_by: tasks.map((t: any) => t.task_created_by),
            task_created_on: tasks.map((t: any) => t.task_created_on),
            task_owner_notified: tasks.map((t: any) => t.task_owner_notified)
          };
        }
      );
      setData(formattedData);
    } catch (error) {
      console.error('Failed to fetch Solutions Tasks data:', error);
    }
  };

  useEffect(() => {
    registerReport(data, columns, 'Solutions_Tasks', 'Solutions & Tasks');
  }, [data, columns, registerReport]);

  useEffect(() => {
    return () => {
      clearReport();
    };
  }, [clearReport]);

  useEffect(() => {
    if (!lang) return;
    if (!orgContext || Object.keys(orgContext).length === 0) return;

    handleDisplay();
  }, [lang, orgContext]);

  return (
    <Styled.VerticalSpace direction='vertical' size='large'>
      <CustomTable
        drawTable={true}
        itemsCount={data.length}
        columns={columns}
        dataSource={data}
        scroll={{ x: 1600, y: 400 }}
        loading={loading}
        rowKey={(record: SolutionTaskData) => record.key}
        sticky
      />
    </Styled.VerticalSpace>
  );
};

export default SolutionsTasks;
